const PRINTABLE_WIDTH = 576;
const DEFAULT_PAPER_WIDTH_MM = 80;
const COMPANY_RUC = '20100366747';
const FONT_WIDTH = { 1: 8, 2: 12, 3: 16 };
const DESCRIPTION_X = 145;
const NEW_COLUMN_TICKET = 340;
const UNIT_PRICE_RIGHT_EDGE = 425;
const LINE_TOTAL_RIGHT_EDGE = 552;
const TOTALS_RIGHT_EDGE = LINE_TOTAL_RIGHT_EDGE;
const DYNAMIC_ADDRESS_MAX_CHARS = 36;
// Las posiciones coinciden con las coordenadas probadas en la TSC Alpha-3R/3RB.
const HORIZONTAL_OFFSET_X = 0;
// Posición de las firmas
const SIGN_LEFT_X = 25;
const SIGN_RIGHT_X = 315;

const normalizeText = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\x20-\x7E]/g, ' ')
        .replace(/\\/g, '/')
        .replace(/"/g, "'")
        .replace(/\s+/g, ' ')
        .trim();
};

const numberValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => numberValue(value).toFixed(2);

const wrapText = (value, maxLength) => {
    const words = normalizeText(value).split(' ').filter(Boolean);
    if (words.length === 0) return [''];

    const lines = [''];
    words.forEach((word) => {
        const wordParts = [];
        for (let index = 0; index < word.length; index += maxLength) {
            wordParts.push(word.substring(index, index + maxLength));
        }

        wordParts.forEach((wordPart, partIndex) => {
            const current = lines[lines.length - 1];
            const separator = current && partIndex === 0 ? ' ' : '';
            if (`${current}${separator}${wordPart}`.length <= maxLength) {
                lines[lines.length - 1] = `${current}${separator}${wordPart}`;
            } else {
                lines.push(wordPart);
            }
        });
    });
    return lines;
};

const textCommand = (x, y, text, font = '2', xScale = 1, yScale = 1) =>
    `TEXT ${Math.max(0, Math.round(x + HORIZONTAL_OFFSET_X))},${Math.round(y)},"${font}",0,${xScale},${yScale},"${normalizeText(text)}"`;

const horizontalPosition = (x) => Math.max(0, Math.round(x + HORIZONTAL_OFFSET_X));

const textWidth = (value, font = '2', xScale = 1) =>
    normalizeText(value).length * (FONT_WIDTH[font] || FONT_WIDTH[2]) * xScale;

const rightAlignedTextCommand = (rightEdge, y, text, font = '2') =>
    textCommand(rightEdge - textWidth(text, font), y, text, font);

const centeredTextCommand = (y, text, font = '2', xScale = 1, yScale = 1, offsetX = 0) =>
    textCommand(
        ((PRINTABLE_WIDTH - textWidth(text, font, xScale)) / 2) + offsetX,
        y,
        text,
        font,
        xScale,
        yScale
    );

const printerCenteredTextCommand = (y, text, font = '2', xScale = 1, yScale = 1) =>
    `TEXT ${PRINTABLE_WIDTH / 2},${Math.round(y)},"${font}",0,${xScale},${yScale},2,"${normalizeText(text)}"`;

const formatDate = (value) => {
    if (!value) return '';
    const normalized = String(value);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}/${match[2]}/${match[3]}`;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return normalizeText(value);
    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0')
    ].join('/');
};

const formatTime = (header, info) => {
    const value = header.CreateTS || header.DocTime || info.CreateTS || '';
    const digits = String(value).replace(/\D/g, '').padStart(4, '0');
    if (!value) {
        const now = new Date();
        return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    }
    if (digits.length >= 6) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:00`;
};

const getDocumentTitle = (header) => {
    const type = String(header.U_BPP_MDTD || '').padStart(2, '0');
    if (type === '01') return {
        title: 'FACTURA ELECTRONICA',
        representation: 'FACTURA ELECTRONICA'
    };
    if (type === '03') return {
        title: 'BOLETA DE VENTA ELECTRONICA',
        representation: 'BOLETA ELECTRONICA'
    };
    return {
        title: '',
        representation: ''
    };
};

const getFolio = (header, info) => {
    const serie = header.U_BPP_MDSD || '';
    const consecutive = header.U_BPP_MDCD || '';
    if (serie || consecutive) return [serie, consecutive].filter(Boolean).join('-');
    return '';
};

const getIdentityType = (header) => {
    const value = String(header.U_BPP_BPTD || '').trim();
    if (value === '6') {
        return 'RUC';
    }
    return 'DNI';
};

const getCurrencyName = (header) => {
    const currency = String(header.CurrencyName || header.currencyName || header.DocCur || '').toUpperCase();
    if (currency === 'SOL' || currency === 'PEN') return 'SOLES';
    if (currency === 'USD') return 'DOLARES';
    return currency || 'SOLES';
};

const getPaymentCondition = (header, payMethods) => {
    const condition = header.PymntGroup || '';
    if (condition) return condition;
    if (Array.isArray(payMethods) && payMethods[0] && payMethods[0].pm_Name) return payMethods[0].pm_Name;
    return 'CONTADO';
};

const getBranchAddressLines = (address) => {
    const normalizedAddress = normalizeText(address).toUpperCase();
    if (!normalizedAddress) return [];

    const valleyMarker = 'HERMOSO ESTE';
    const valleyIndex = normalizedAddress.indexOf(valleyMarker);
    if (valleyIndex >= 0) {
        const firstPart = normalizedAddress.substring(0, valleyIndex).trim();
        const lastPart = normalizedAddress.substring(valleyIndex + valleyMarker.length).trim();
        return [
            ...wrapText(firstPart, DYNAMIC_ADDRESS_MAX_CHARS),
            valleyMarker,
            ...wrapText(lastPart, DYNAMIC_ADDRESS_MAX_CHARS)
        ].filter(Boolean);
    }
    return wrapText(normalizedAddress, DYNAMIC_ADDRESS_MAX_CHARS);
};

const getLineDescription = (line) =>
    line.Dscription || line.ItemName || line.itemName || line.ItemCode || '';

const getLineQuantity = (line) =>
    numberValue(line.Quantity !== undefined ? line.Quantity : line.quantity);

const getLinePrice = (line) => {
    if (line?.TaxCode == 'EXO') {
        return 0
    } else {
        return numberValue(line.PriceBefDi !== undefined ? line.PriceBefDi : (line.Price !== undefined ? line.Price : line.PriceAfVAT));

    }
}

const getLineDiscountPercent = (line) =>
    numberValue(line.DiscPrcnt !== undefined ? line.DiscPrcnt : line.discPrcnt);

const getLineUnitDiscount = (line) =>
    (getLinePrice(line) * getLineDiscountPercent(line)) / 100;

const getLineTotal = (line) => {
    const priceAfterDiscount = getLinePrice(line) - getLineUnitDiscount(line);
    return priceAfterDiscount * getLineQuantity(line);
};

const getLineTaxStatus = (line) =>
    String(line.TaxStatus || 'Y').toUpperCase();

const getLineTaxOnly = (line) =>
    String(line.TaxOnly || 'N').toUpperCase();

const getLineOperation = (line) =>
    String(line.U_BPP_OPER || line.uBppOper || line.OperationType || '').toUpperCase();

const getLineTaxCode = (line) =>
    String(line.TaxCode || line.taxCode || line.VatGroup || '').toUpperCase();

const getTaxAmount = (taxCode, amount) => {
    switch (taxCode) {
        case 'EXO':
            return 0;
        case 'IGV':
        case 'IGVPER05':
        case 'IGVPER':
        default:
            return amount * 0.18;
    }
};

const getPerceptionAmount = (taxCode, amountWithTax) => {
    switch (taxCode) {
        case 'IGVPER05':
            return amountWithTax * 0.005;
        case 'IGVPER':
            return amountWithTax * 0.02;
        case 'IGV':
        case 'EXO':
        default:
            return 0;
    }
};

const calculateTotals = (lines) => {
    const totals = lines.reduce((result, line) => {
        // TaxStatus = Sujeto a impuestos, si viene que si suma los impuestos, si viene que no no suma los impuestos
        // TaxOnly = Solo impuestos, si viene que si solo suma los impuestos, si viene que no suma el total mas impuestos
        const amount = getLineTotal(line);
        const operation = getLineOperation(line);
        const taxCode = getLineTaxCode(line);
        const taxStatus = getLineTaxStatus(line);
        const taxOnly = getLineTaxOnly(line);
        const tax = taxStatus === 'Y' ? getTaxAmount(taxCode, amount) : 0;

        // Si TaxOnly = 'Y', NO sumamos el importe de la linea solamente el impuesto
        const amountToAdd = taxOnly === 'Y' ? 0 : amount;

        if (operation === 'E') result.exonerated += amountToAdd;
        if (operation === 'I') result.unaffected += amountToAdd;
        if (operation === 'A') result.taxed += amountToAdd;

        // Si es TaxOnly tampoco sumamos descuento al documento
        if (taxOnly !== 'Y') {
            result.discount += getLineQuantity(line) * getLineUnitDiscount(line);
        }

        result.subtotal += amountToAdd;

        // aqui va el puro Impuesto
        result.vat += tax;

        // Percepción
        if (taxStatus === 'Y') {
            result.perception += getPerceptionAmount(taxCode, amount + tax);
        }

        return result;
    }, {
        exonerated: 0,
        unaffected: 0,
        taxed: 0,
        discount: 0,
        subtotal: 0,
        vat: 0,
        perception: 0
    });

    return {
        ...totals,
        documentTotal: totals.subtotal + totals.vat,
        totalToPay: totals.subtotal + totals.vat + totals.perception
    };
};

const integerToSpanish = (value) => {
    const units = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const special = {
        10: 'DIEZ', 11: 'ONCE', 12: 'DOCE', 13: 'TRECE', 14: 'CATORCE', 15: 'QUINCE',
        16: 'DIECISEIS', 17: 'DIECISIETE', 18: 'DIECIOCHO', 19: 'DIECINUEVE',
        20: 'VEINTE', 21: 'VEINTIUN', 22: 'VEINTIDOS', 23: 'VEINTITRES', 24: 'VEINTICUATRO',
        25: 'VEINTICINCO', 26: 'VEINTISEIS', 27: 'VEINTISIETE', 28: 'VEINTIOCHO', 29: 'VEINTINUEVE'
    };
    const tens = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const hundreds = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

    const underThousand = (number) => {
        if (number === 0) return '';
        if (number === 100) return 'CIEN';
        const hundred = Math.floor(number / 100);
        const remainder = number % 100;
        let result = hundred ? hundreds[hundred] : '';
        if (remainder) {
            const remainderText = remainder < 10
                ? units[remainder]
                : special[remainder] || `${tens[Math.floor(remainder / 10)]}${remainder % 10 ? ` Y ${units[remainder % 10]}` : ''}`;
            result = `${result}${result ? ' ' : ''}${remainderText}`;
        }
        return result;
    };

    const number = Math.max(0, Math.floor(numberValue(value)));
    if (number === 0) return 'CERO';
    if (number < 1000) return underThousand(number);
    if (number < 1000000) {
        const thousands = Math.floor(number / 1000);
        const remainder = number % 1000;
        return `${thousands === 1 ? 'MIL' : `${underThousand(thousands)} MIL`}${remainder ? ` ${underThousand(remainder)}` : ''}`;
    }
    const millions = Math.floor(number / 1000000);
    const remainder = number % 1000000;
    return `${millions === 1 ? 'UN MILLON' : `${integerToSpanish(millions)} MILLONES`}${remainder ? ` ${integerToSpanish(remainder)}` : ''}`;
};

const amountInWords = (amount, currencyName) => {
    const numericAmount = numberValue(amount);
    const totalCents = Math.round(numericAmount * 100);
    const integer = Math.floor(totalCents / 100);
    const cents = totalCents % 100;
    return `SON ${integerToSpanish(integer)} CON ${String(cents).padStart(2, '0')}/100 ${currencyName}`;
};

const createTestDocument = () => {
    const now = new Date();
    const date = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
    ].join('-');
    const time = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;

    return {
        testTicket: true,
        docHeader: {
            responseHeader: {
                U_BPP_MDTD: '03',
                U_BPP_MDSD: 'B049',
                U_BPP_MDCD: '0001847',
                U_BPP_BPTD: '1',
                U_BPP_TtPc: 0,
                DocDate: date,
                DocDueDate: date,
                CreateTS: time,
                PymntGroup: 'CONTADO',
                DocCur: 'SOL',
                CardName: 'JESSICA YOLY PALOMINO CHAVEZ',
                LicTradNum: '29659536',
                DocTotal: 65,
                VatSum: 9.92,
                DiscSum: 0,
                U_FMB_User: '958834085'
            },
            infoByFolio: { folio: 'B049-0001847', DocDate: date },
            payMethods: [{ pm_Name: 'CONTADO', amount: 65 }]
        },
        docLines: [{
            Quantity: 1,
            SalUnitMsr: 'KG',
            Dscription: 'GLP ENVASADO A 10 KG C',
            Price: 55.0847457627,
            LineTotal: 55.0847457627,
            TaxCode: 'IGV',
            U_BPP_OPER: 'A'
        }]
    };
};

export const buildInvoiceTicket = (document, user = {}, configuration = {}) => {
    if (!document || !document.docHeader) {
        throw new Error('El documento no contiene la informacion necesaria para construir el ticket.');
    }

    const header = document.docHeader.responseHeader || {};
    const info = document.docHeader.infoByFolio || {};
    const payMethods = Array.isArray(document.docHeader.payMethods) ? document.docHeader.payMethods : [];
    const lines = Array.isArray(document.docLines) ? document.docLines : [];
    const branch = user.Branch || user.branch || {};
    const company = user.Timbra || user.timbra || {};
    const paperWidth = Number(configuration.paperWidthMm) || DEFAULT_PAPER_WIDTH_MM;
    const documentTitle = getDocumentTitle(header);
    const currencyName = getCurrencyName(header);
    const totals = calculateTotals(lines);
    // Aqui suma la perción cuando es a credito (segun el Vic)
    if (Number(header.U_BPP_TtPc) > 0 && header.GroupNum !== -1) {
        totals.perception += Number(header.U_BPP_TtPc);
        totals.totalToPay += Number(header.U_BPP_TtPc);
    }
    const commands = [];
    let y = 18;

    const addCentered = (value, options = {}) => {
        const {
            font = '2', lineHeight = 27, maxLength = 42, offsetX = 0
        } = options;
        wrapText(value, maxLength).forEach((line) => {
            commands.push(centeredTextCommand(y, line, font, 1, 1, offsetX));
            y += lineHeight;
        });
    };

    const addPrinterCentered = (value, options = {}) => {
        const { font = '2', lineHeight = 27, maxLength = 42 } = options;
        wrapText(value, maxLength).forEach((line) => {
            commands.push(printerCenteredTextCommand(y, line, font));
            y += lineHeight;
        });
    };

    const addLeft = (value, options = {}) => {
        const { x = 10, font = '2', lineHeight = 27, maxLength = 42 } = options;
        wrapText(value, maxLength).forEach((line) => {
            commands.push(textCommand(x, y, line, font));
            y += lineHeight;
        });
    };

    const addDashedSeparator = (font = '1') => {
        commands.push(centeredTextCommand(y, '-'.repeat(font === '1' ? 69 : 46), font));
        y += font === '1' ? 18 : 25;
    };

    const addTotal = (label, value) => {
        const formattedValue = money(value);
        commands.push(textCommand(20, y, label, '2'));
        commands.push(rightAlignedTextCommand(TOTALS_RIGHT_EDGE, y, formattedValue, '2'));
        y += 27;
    };

    if (document.testTicket) {
        addCentered('*** PRUEBA DE FORMATO - SIN VALOR ***', { font: '1', lineHeight: 25, maxLength: 69 });
        y += 5;
    }

    addPrinterCentered('LLAMA GAS S.A.', { font: '3', lineHeight: 35, maxLength: 35 });
    addPrinterCentered('AV. EL POLO NRO. 397 (ESQ.', { font: '2', maxLength: 48 });
    addPrinterCentered('JR. EMANCIPACION)', { font: '2', maxLength: 48 });
    addPrinterCentered('SANTIAGO DE SURCO - LIMA LIMA', { font: '2', maxLength: 48 });

    const branchAddress = branch.Address || branch.address || '';
    getBranchAddressLines(branchAddress).forEach((addressLine) => {
        addPrinterCentered(addressLine, {
            font: '2',
            maxLength: DYNAMIC_ADDRESS_MAX_CHARS
        });
    });
    addPrinterCentered('Telefono: 617-1717', { font: '2' });
    y += 4;

    commands.push(textCommand(10, y, `RUC: ${company.RFC || company.rfc || COMPANY_RUC}`, '2'));
    y += 25;
    addDashedSeparator('1');
    addCentered(documentTitle.title, { font: '3', lineHeight: 34, maxLength: 35 });
    addCentered(getFolio(header, info), { font: '2', lineHeight: 27 });
    addDashedSeparator('1');

    const emissionDate = formatDate(info.DocDate || header.DocDate || new Date());
    const dueDate = formatDate(header.DocDueDate || info.DocDate || header.DocDate);
    commands.push(textCommand(10, y, `F.Emi:${emissionDate} ${formatTime(header, info)}`, '2'));
    y += 27;
    commands.push(textCommand(10, y, `F.Ven:${dueDate}`, '2'));
    commands.push(textCommand(270, y, `Cond.Pago:${getPaymentCondition(header, payMethods)}`, '2'));
    y += 27;
    commands.push(textCommand(10, y, `Moneda:${currencyName}`, '2'));
    y += 25;
    addDashedSeparator('1');

    commands.push(textCommand(10, y, `Doc.Identidad:${getIdentityType(header)}`, '2'));
    commands.push(textCommand(270, y, `Numero:${header.LicTradNum || ''}`, '2'));
    y += 27;
    addLeft(header.CardName || '', { font: '2', maxLength: 42 });
    addDashedSeparator('1');

    commands.push(textCommand(10, y, 'CANT', '1'));
    commands.push(textCommand(90, y, 'U.M.', '1'));
    commands.push(textCommand(DESCRIPTION_X, y, 'DESCRIPCION', '1'));
    commands.push(rightAlignedTextCommand(UNIT_PRICE_RIGHT_EDGE, y, 'P.UNIT', '1'));
    commands.push(rightAlignedTextCommand(LINE_TOTAL_RIGHT_EDGE, y, 'IMPORTE', '1'));
    y += 18;
    addDashedSeparator('1');

    lines.forEach((line) => {
        const rowY = y;
        commands.push(textCommand(20, rowY, money(getLineQuantity(line)), '1'));
        commands.push(textCommand(90, rowY, line.SalUnitMsr || line.MeasureUnit || line.UM || '', '1'));
        const descriptions = wrapText(getLineDescription(line), 20).slice(0, 3);
        descriptions.forEach((description, index) => {
            commands.push(textCommand(DESCRIPTION_X, rowY + (index * 18), description, '1'));
        });
        commands.push(rightAlignedTextCommand(
            UNIT_PRICE_RIGHT_EDGE,
            rowY,
            money(getLinePrice(line)),
            '1'
        ));
        commands.push(rightAlignedTextCommand(
            LINE_TOTAL_RIGHT_EDGE,
            rowY,
            money(getLineTotal(line)),
            '1'
        ));
        y += Math.max(22, descriptions.length * 18);
    });

    addDashedSeparator('1');
    addTotal('OP. EXONERADAS', totals.exonerated);
    addTotal('OP. INAFECTAS', totals.unaffected);
    addTotal('OP. GRAVADAS', totals.taxed);
    addTotal('TOTAL DESCUENTOS', totals.discount);
    addTotal('I.G.V.', totals.vat);
    addTotal('IMPORTE TOTAL', totals.documentTotal);
    addTotal('PERCEPCION', totals.perception);
    addTotal('TOTAL A PAGAR', totals.totalToPay);
    addDashedSeparator('1');

    addLeft(amountInWords(totals.totalToPay, currencyName), { font: '1', lineHeight: 19, maxLength: 67 });
    addDashedSeparator('1');

    const cashier = user.USER_CODE || '';
    addLeft(`Cobrado por: ${cashier}`, { font: '2', maxLength: 45 });
    y += 8;

    const qrValue = [
        COMPANY_RUC,
        header.U_BPP_MDTD || '',
        header.U_BPP_MDCD || '',
        header.U_BPP_MDSD || '',
        money(totals.vat),
        money(totals.documentTotal + totals.vat),
        emissionDate,
        1,
        header.LicTradNum || ''
    ].join('|');
    // Posicion usada por el formato Dart que ya fue probado en esta impresora.
    commands.push(`QRCODE 190,${y},L,6,A,0,"${normalizeText(qrValue)}"`);
    y += 215;

    // La impresora centra estas lineas con sus propias metricas de fuente TSPL.
    commands.push(printerCenteredTextCommand(y, 'Representacion Impresa de la', '2'));
    y += 27;
    commands.push(printerCenteredTextCommand(y, documentTitle.representation, '3'));
    y += 34;
    y += 8;

    addLeft(
        'Consulta en https://portal2.efacturacion.pe/visorBoleta/ o https://portal2.efacturacion.pe/appefacturacion/',
        { font: '1', lineHeight: 19, maxLength: 64 }
    );
    y += 5;
    addLeft('Incorporado al Regimen de Agentes de Percepcion de IGV', {
        font: '1', lineHeight: 19, maxLength: 64
    });
    addLeft('- Venta Interna (R.S.058-2006) a partir del 01/04/2006', {
        font: '1', lineHeight: 19, maxLength: 64
    });
    addLeft('Incorporado al Regimen de Agentes de Retencion de IGV', {
        font: '1', lineHeight: 19, maxLength: 64
    });
    addLeft('(RS 037-2002) a partir del 01/06/2002', {
        font: '1', lineHeight: 19, maxLength: 64
    });
    addLeft(
        'Autorizado mediante resolucion N 0180050001451/SUNAT',
        { font: '1', lineHeight: 19, maxLength: 64 }
    );
    y += 35;

    addCentered('Vencido el plazo, se', { font: '3', lineHeight: 31, maxLength: 35 });
    addCentered('cobrara interes moratorio', { font: '3', lineHeight: 31, maxLength: 35 });
    addCentered('y compensatorio', { font: '3', lineHeight: 31, maxLength: 35 });
    y += 30;
    addCentered('MUCHAS GRACIAS POR SU COMPRA', { font: '3', lineHeight: 34, maxLength: 35 });
    commands.push(`BAR ${horizontalPosition(15)},${y},546,2`);
    y += 45;

    const heightMm = Math.max(100, Math.ceil(y / 8));
    return [
        `SIZE ${paperWidth} mm, ${heightMm} mm`,
        'GAP 0 mm, 0 mm',
        'DIRECTION 0',
        'CLS',
        ...commands,
        'PRINT 1',
        ''
    ].join('\r\n');
};

export const buildTicketPreview = (configuration = {}) => buildInvoiceTicket(
    createTestDocument(),
    {
        Branch: {
            Address: 'AV. PRIMAVERA NRO. 1282 URB. VALLE HERMOSO ESTE SANTIAGO DE SURCO, LIMA, LIMA',
            Phone: '617-1717'
        },
        Timbra: { RFC: COMPANY_RUC }
    },
    configuration
);

export const buildTestTicket = (configuration = {}) => {
    const width = Number(configuration.paperWidthMm) || DEFAULT_PAPER_WIDTH_MM;
    return [
        `SIZE ${width} mm, 35 mm`,
        'GAP 0 mm, 0 mm',
        'DIRECTION 0',
        'CLS',
        centeredTextCommand(25, 'PRUEBA DE IMPRESION', '3'),
        centeredTextCommand(70, 'COUNTER ONE - TSPL', '3'),
        centeredTextCommand(120, 'Conexion Web Serial correcta', '2'),
        centeredTextCommand(155, new Date().toLocaleString(), '2'),
        `BAR ${horizontalPosition(15)},195,546,2`,
        centeredTextCommand(215, 'TSC ALPHA-3R / 3RB', '2'),
        'PRINT 1',
        ''
    ].join('\r\n');
};

export const buildTicket = (document, user = {}, configuration = {}) => {

    if (!document || !document.header) {
        throw new Error('El documento no contiene la informacion necesaria para construir el ticket.');
    }

    const header = document.header || {};
    const isMercancia = document.DocType === 'OWTR' || document.DocType === 'OIGE';
    const lines = Array.isArray(document.body) ? document.body : [];
    // Descomentar si se requere dirección de la sucursal dinamica
    // const branch = user.Branch || user.branch || {};
    const paperWidth = Number(configuration.paperWidthMm) || DEFAULT_PAPER_WIDTH_MM;
    const commands = [];
    let y = 18;

    const addPrinterCentered = (value, options = {}) => {
        const { font = '2', lineHeight = 27, maxLength = 42 } = options;
        wrapText(value, maxLength).forEach((line) => {
            commands.push(printerCenteredTextCommand(y, line, font));
            y += lineHeight;
        });
    };

    const addLeft = (value, options = {}) => {
        const { x = 10, font = '2', lineHeight = 27, maxLength = 42 } = options;
        wrapText(value, maxLength).forEach((line) => {
            commands.push(textCommand(x, y, line, font));
            y += lineHeight;
        });
    };

    // const addName = (value, options = {}) => {
    //     const { x = 10, font = '2', lineHeight = 27, maxLength = 42 } = options;
    //     wrapText(value, maxLength).forEach((line, index) => {
    //         const prefix = index === 0 ? 'RAZÓN SOCIAL: ' : '';
    //         commands.push(
    //             textCommand(x, y, `${prefix}${line || ''}`, font)
    //         );
    //         y += lineHeight;
    //     });
    // };

    const addDashedSeparator = (font = '1') => {
        commands.push(centeredTextCommand(y, '-'.repeat(font === '1' ? 69 : 46), font));
        y += font === '1' ? 18 : 25;
    };
    const DOC_TYPE_LABELS = {
        '20': 'ENTRADA DE MERCANCIA:',
        '67': 'TRASLADO DE MERCANCIA:',
        '60': 'SALIDA DE MERCANCIA:',
    };
    const docLabel = DOC_TYPE_LABELS[header.ObjType] ?? 'DOCUMENTO:';

    addPrinterCentered('LLAMAGAS', { font: '3', lineHeight: 35, maxLength: 35 });
    addPrinterCentered('(01) 617-1717', { font: '2', maxLength: 48 });
    addPrinterCentered('Avenida El Polo 397, Santiago de Surco', { font: '2', maxLength: 48 });
    addPrinterCentered('Lima 15023, PE', { font: '2', maxLength: 48 });

    // DESCOMENTAR SI QUIEREN LA DIRECCIÓN DE LA SUCURSAL COMO EN LOS OTROS TICKETS :)
    // const branchAddress = branch.Address || branch.address || '';
    // getBranchAddressLines(branchAddress).forEach((addressLine) => {
    //     addPrinterCentered(addressLine, {
    //         font: '2',
    //         maxLength: DYNAMIC_ADDRESS_MAX_CHARS
    //     });
    // });
    // y += 4;

    addDashedSeparator('1');

    const taxDate = formatDate(header.TaxDate || new Date());
    const hasReferenceData = header?.U_BPP_MDTD || header?.U_BPP_MDSD || header?.U_BPP_MDCD;
    const referencia = hasReferenceData ? `${header?.U_BPP_MDTD ?? ''}-${header?.U_BPP_MDSD ?? ''}-${header?.U_BPP_MDCD ?? ''}`.replace(/-+/g, '-') : '';
    const documentoValue = lines[0]?.OC_DocDate ? `${formatDate(lines[0].OC_DocDate)} O.C.${lines[0]?.BaseRef ?? ''}` : '';

    commands.push(textCommand(10, y, `FECHA: ${taxDate}`, '2'));
    y += 27;
    commands.push(textCommand(10, y, `${docLabel} ${header.DocNum}`, '2'));
    y += 27;
    // Solo para entrada de mercancia compras
    if (document.DocType === 'OPDN') {
        commands.push(textCommand(10, y, `DOCUMENTO: ${documentoValue}`, '2'));
        y += 25;
    }
    commands.push(textCommand(10, y, `REFERENCIA: ${referencia}`, '2'));
    y += 25;
    commands.push(textCommand(10, y, `MONEDA: ${header?.DocCur}`, '2'));
    y += 25;

    // addName(header.CardName || '', { font: '2', maxLength: 42 });
    if (document.DocType !== 'OIGE') {
        addLeft(`RAZÓN SOCIAL: ${header.CardName || ''}`, { font: '2', maxLength: 42 });
        commands.push(textCommand(10, y, `RUC:${header.LicTradNum || ''}`, '2'));
        y += 25;
    }
    addDashedSeparator('1');

    commands.push(textCommand(10, y, 'ARTICULO', '1'));
    commands.push(textCommand(DESCRIPTION_X, y, 'DESCRIPCION', '1'));
    commands.push(rightAlignedTextCommand(NEW_COLUMN_TICKET, y, 'UNIDAD', '1'));
    commands.push(rightAlignedTextCommand(UNIT_PRICE_RIGHT_EDGE + 20, y, 'CANTIDAD', '1'));
    if (!isMercancia) {
        commands.push(rightAlignedTextCommand(UNIT_PRICE_RIGHT_EDGE + 20, y + 18, 'INGRESO', '1'));
    }
    // commands.push(rightAlignedTextCommand(LINE_TOTAL_RIGHT_EDGE, y, 'STOCK ACTUAL', '1'));
    // y += 18;
    commands.push(rightAlignedTextCommand(LINE_TOTAL_RIGHT_EDGE, y, 'STOCK', '1'));
    commands.push(rightAlignedTextCommand(LINE_TOTAL_RIGHT_EDGE, y + 18, 'ACTUAL', '1'));
    y += 36;
    addDashedSeparator();

    lines.forEach((line) => {
        const rowY = y;
        const cantidadIngreso = line.CantidadIngreso ?? line.Quantity;
        // const cantidadPendiente = line.CantidadPendiente ?? line.OpenCreQty;

        commands.push(textCommand(20, rowY, line.ItemCode, '1'));
        // commands.push(textCommand(90, rowY, line.SalUnitMsr || line.MeasureUnit || line.UM || '', '1'));
        const descriptions = wrapText(getLineDescription(line), 20).slice(0, 3);
        descriptions.forEach((description, index) => {
            commands.push(textCommand(DESCRIPTION_X, rowY + (index * 18), description, '1'));
        });

        // commands.push(rightAlignedTextCommand(NEW_COLUMN_TICKET + 5, rowY, line.SalUnitMsr || line.MeasureUnit || line.UM || '', '1'));
        commands.push(rightAlignedTextCommand(NEW_COLUMN_TICKET + 5, rowY, line.InvntryUom || '', '1'));
        commands.push(rightAlignedTextCommand(UNIT_PRICE_RIGHT_EDGE + 20, rowY, isMercancia ? Number(line.Quantity ?? line.CantidadIngreso).toFixed(2) : Number(cantidadIngreso ?? 0).toFixed(2), '1'));
        commands.push(rightAlignedTextCommand(LINE_TOTAL_RIGHT_EDGE, rowY, line.StockActual ?? '', '1'));
        y += Math.max(22, descriptions.length * 18);
    });

    addDashedSeparator('1');
    y += 8;

    const creatorName = header?.CreadorNombre || header?.U_FMB_Handel_Creador || header?.U_FMB_User || ''
    commands.push(textCommand(10, y, `Recibido por: ${creatorName}`, '1'));
    commands.push(rightAlignedTextCommand(LINE_TOTAL_RIGHT_EDGE, y, 'LOGISTICA', '1'));
    y += 18;
    y += 18;

    // FIRMAS
    const addCenteredLeftSignature = (text, currentY) => {
        const LEFT_LIMIT = SIGN_LEFT_X;
        const RIGHT_LIMIT = 270;
        const CHAR_WIDTH = 8;

        const blockWidth = RIGHT_LIMIT - LEFT_LIMIT;
        const textWidth = String(text).length * CHAR_WIDTH;

        const x = LEFT_LIMIT + ((blockWidth - textWidth) / 2);

        commands.push(
            textCommand(Math.max(LEFT_LIMIT, x), currentY, text, '1')
        );
    };

    const addCenteredRightSignature = (text, currentY) => {
        const LEFT_LIMIT = 300;
        const RIGHT_LIMIT = LINE_TOTAL_RIGHT_EDGE;
        const CHAR_WIDTH = 8;

        const blockWidth = RIGHT_LIMIT - LEFT_LIMIT;
        const textWidth = String(text).length * CHAR_WIDTH;

        const x = LEFT_LIMIT + ((blockWidth - textWidth) / 2);

        commands.push(
            textCommand(Math.max(LEFT_LIMIT, x), currentY, text, '1')
        );
    };
    const showPartyBlock = document.DocType !== 'OIGE';

    y += 30;
    commands.push(textCommand(SIGN_LEFT_X, y, '---------------------------', '1'));
    if (showPartyBlock) {
        commands.push(textCommand(SIGN_RIGHT_X, y, '---------------------------', '1'));
    }
    y += 18;

    addCenteredLeftSignature('Registrado Por:', y);
    if (showPartyBlock) {
        addCenteredRightSignature('DATOS DEL PROVEEDOR', y);
    }
    y += 18;

    const registeredLines = wrapText(creatorName || '', 20).slice(0, 2);

    registeredLines.forEach((line, index) => {
        addCenteredLeftSignature(line, y + (index * 18));
    });

    const providerLines = showPartyBlock ? wrapText(header.CardName || '', 25).slice(0, 3) : [];

    if (showPartyBlock) {
        providerLines.forEach((line, index) => {
            addCenteredRightSignature(line, y + (index * 18));
        });
    }

    y += Math.max(18, registeredLines.length * 18, providerLines.length * 18);

    addCenteredLeftSignature('ENCARGADO (A) DE ALMACEN', y);
    y += 25;
    // FIRMAS

    const heightMm = Math.max(100, Math.ceil(y / 8));
    return [
        `SIZE ${paperWidth} mm, ${heightMm} mm`,
        'GAP 0 mm, 0 mm',
        'DIRECTION 0',
        'CLS',
        ...commands,
        'PRINT 1',
        ''
    ].join('\r\n');
};

export default {
    buildTestTicket,
    buildTicketPreview,
    buildInvoiceTicket,
    buildTicket
};
